import { ArrowBack, ArrowDropDownRounded, ArrowRightRounded, ExpandLess, ExpandMore, FilterListRounded } from '@mui/icons-material'
import { Box, Collapse, ListItemText, Typography } from '@mui/material'
import clsx from 'clsx'
import { SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { setGlobalBrand, setGlobalFamily, setGlobalLine, setGlobalTag } from '../../../../../redux/actions/global'
import { IRootState } from '../../../../../redux/types'
import { ROLES } from '../../../../backoffice/users/models/user'
import useScreenWidth from '../../../hooks/useScreenWidth'
import {
  BorderSideNav,
  ButtonClearFilters,
  ChipLine,
  FamilyItemButton,
  ItemClearFilter,
  LineTreeItem,
  StyledTreeItem,
  StyledTreeView
} from '../../../styles/BrandFilterStyles'
import ListItemMenuButton from '../../../styles/ListItemMenu'
import TagFilter from '../../filters/TagFilter'

function BrandFilter({ layout }: { layout: string }) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(search)
  const dispatch = useDispatch()

  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { userFamilies, userBrands } = useSelector((state: IRootState) => state.brand)
  const { globalFamilyId, globalBrandId, globalLineId } = useSelector((state: IRootState) => state.global)

  const [open, setOpen] = useState(true)
  const [expanded, setExpanded] = useState<string[]>([`B${localStorage.getItem('globalBrandId') || 'null'}`])
  const [selected, setSelected] = useState<string>(`L${localStorage.getItem('globalLineId') || 'null'}`)

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
  useEffect(() => {
    if (pathname === '/novedades' || pathname === '/noticias') {
      handleGlobalBrand(null)
    } else if (search && searchParams.get('globalFamilyId') !== null && searchParams.get('globalFamilyId') !== 'null') {
      if (search && searchParams.get('globalBrandId') !== null && searchParams.get('globalBrandId') !== 'null') {
        let userBrand = userBrands.find((brand: any) => brand.id === Number(searchParams.get('globalBrandId')))
        if (userBrand !== undefined) {
          if (
            searchParams.get('globalLineId') !== null &&
            userBrand.active_lines.find((brandline: any) => brandline.id === Number(searchParams.get('globalLineId')))
          ) {
            handleGlobalFamilyBrandLine(
              Number(searchParams.get('globalFamilyId')),
              Number(searchParams.get('globalBrandId')),
              Number(searchParams.get('globalLineId'))
            )
          } else {
            handleGlobalFamilyBrandLine(Number(searchParams.get('globalFamilyId')), Number(searchParams.get('globalBrandId')))
          }
        } else {
          handleGlobalFamilyBrandLine(userBrands[0].family_id, userBrands[0].id, null)
        }
      } else {
        handleGlobalFamilyBrandLine(Number(searchParams.get('globalFamilyId')), null, null)
      }
    } else {
      handleGlobalFamilyBrandLine(null, null, null)
    }
  }, [])

  useEffect(() => {
    setExpanded([`B${globalBrandId}`])
    setSelected(`L${globalLineId}`)
  }, [globalBrandId, globalLineId])

  const handleClick = () => {
    setOpen(!open)
  }

  const clearFilters = () => {
    setOpen(false)
    handleGlobalFamily(null)
  }

  const handleToggle = (_: SyntheticEvent, nodeIds: string[]) => {
    setExpanded([nodeIds[0]])
  }

  const handleSelect = (_: SyntheticEvent, nodeIds: string | string[]) => {
    setSelected(nodeIds[0] ?? nodeIds)
  }

  const handleGlobalFamily = (familyId: number | null) => {
    localStorage.setItem('globalFamilyId', familyId === null ? 'null' : familyId.toString())
    dispatch(setGlobalFamily(familyId))
    searchParams.set('globalFamilyId', familyId === null ? 'null' : familyId.toString())
    handleGlobalBrand(null)
    setExpanded(['Bnull'])
    setSelected('Lnull')
    setOpen(true)
  }

  const handleGlobalBrand = (brandId: number | null) => {
    localStorage.setItem('globalLineId', 'null')
    dispatch(setGlobalLine(null))
    searchParams.delete('globalLineId')

    localStorage.setItem('globalTag', 'null')
    dispatch(setGlobalTag(null))
    searchParams.delete('globalTag')

    localStorage.setItem('globalBrandId', brandId === null ? 'null' : brandId.toString())
    dispatch(setGlobalBrand(brandId))
    searchParams.set('globalBrandId', brandId === null ? 'null' : brandId.toString())
    navigate(`${pathname}?${searchParams}`, { replace: true })
  }

  const handleGlobalLine = (lineId: number) => {
    localStorage.setItem('globalLineId', lineId.toString())
    dispatch(setGlobalLine(lineId))
    searchParams.set('globalLineId', lineId.toString())
    navigate(`${pathname}?${searchParams}`, { replace: true })
  }

  const handleGlobalFamilyBrandLine = (familyId: number | null, brandId: number | null, lineId: number | null = null) => {
    localStorage.setItem('globalFamilyId', familyId === null ? 'null' : familyId.toString())
    dispatch(setGlobalFamily(familyId))
    searchParams.set('globalFamilyId', familyId === null ? 'null' : familyId.toString())

    localStorage.setItem('globalBrandId', brandId === null ? 'null' : brandId.toString())
    dispatch(setGlobalBrand(brandId))
    searchParams.set('globalBrandId', brandId === null ? 'null' : brandId.toString())

    localStorage.setItem('globalLineId', lineId === null ? 'null' : lineId.toString())
    dispatch(setGlobalLine(lineId))
    searchParams.set('globalLineId', lineId === null ? 'null' : lineId.toString())

    navigate(`${pathname}?${searchParams}`, { replace: true })
  }

  return (
    <BorderSideNav id="sidebar" className={layout}>
      {globalFamilyId != null && (
        <ItemClearFilter className={layout}>
          <FilterListRounded />
          <ButtonClearFilters
            id="btn-clear-filters"
            color="inherit"
            variant="outlined"
            size="small"
            className={layout}
            onClick={clearFilters}
          >
            {t('clean-filters')}
          </ButtonClearFilters>
        </ItemClearFilter>
      )}

      {userFamilies.map((family: any) => (
        <FamilyItemButton
          key={family.id}
          onClick={() => handleGlobalFamily(family.id)}
          className={clsx(
            globalFamilyId != null ? 'small' : '',
            globalFamilyId === family.id || globalFamilyId === null ? family.code : '',
            layout
          )}
        >
          {isMobile || t(`${family.code}`).length > 16 ? (
            <Typography>{`${t(`${family.code}`).substring(0, 16).toUpperCase()}.`}</Typography>
          ) : (
            <Typography>{t(`${family.code}`).toUpperCase()}</Typography>
          )}
        </FamilyItemButton>
      ))}

      {layout === 'client' && <TagFilter layout={layout} />}

      {globalFamilyId != null && (
        <ListItemMenuButton onClick={() => handleClick()} className={layout}>
          <ListItemText primary={t('brand')} />
          {open ? <ExpandLess color="primary" fontSize="small" /> : <ExpandMore color="primary" fontSize="small" />}
        </ListItemMenuButton>
      )}

      {globalFamilyId != null && (
        <Collapse in={open} timeout="auto" unmountOnExit style={{ width: '100%' }}>
          <StyledTreeView
            id="tree-brand-filter"
            expanded={expanded}
            selected={selected}
            defaultCollapseIcon={<ArrowDropDownRounded fontSize="large" />}
            defaultExpandIcon={<ArrowRightRounded fontSize="large" />}
            defaultParentIcon={<ArrowBack />}
            className={layout}
            onNodeToggle={handleToggle}
            onNodeSelect={handleSelect}
          >
            <StyledTreeItem
              classes={{
                root: layout,
                selected: layout
              }}
              className={'brand'}
              nodeId={'Bnull'}
              key={'without-brand'}
              onClick={() => handleGlobalBrand(null)}
              label={
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {t('all')}
                </Typography>
              }
            />

            {userBrands
              .filter((brand: any) => brand.familiesIds.includes(globalFamilyId))
              .map((brand: any) => (
                <>
                  {/* Return TreeItem without lines ELSE return with lines */}
                  {brand.active_lines.length === 0 ? (
                    <StyledTreeItem
                      key={brand.id}
                      classes={{
                        root: layout,
                        expanded: layout,
                        selected: layout
                      }}
                      className={'brand'}
                      nodeId={`B${brand.id}`}
                      onClick={() => handleGlobalBrand(brand.id)}
                      label={
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {brand.name}
                        </Typography>
                      }
                    />
                  ) : (
                    <StyledTreeItem
                      key={brand.id}
                      classes={{
                        root: layout,
                        expanded: layout,
                        selected: layout
                      }}
                      className={'brand'}
                      nodeId={`B${brand.id}`}
                      onClick={() => handleGlobalBrand(brand.id)}
                      label={
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {brand.name}
                        </Typography>
                      }
                    >
                      {/* With Brandlines */}
                      {brand.active_lines
                        .filter((brandline: any) => authUser.brandlines.includes(brandline.id) || authUser.role_id === 1)
                        .map((line: any) => (
                          <LineTreeItem
                            key={line.id}
                            onClick={() => handleGlobalLine(line.id)}
                            classes={{
                              root: layout,
                              selected: layout
                            }}
                            nodeId={`L${line.id}`}
                            label={
                              <Box display="flex" alignItems="center">
                                <ChipLine classes={{ root: layout }} />
                                <Typography variant="body2">{line.name}</Typography>
                              </Box>
                            }
                          />
                        ))}

                      {/* Without Brandlines */}
                      {brand.active_lines.filter(
                        (brandline: any) => authUser.brandlines.includes(brandline.id) || authUser.role_id === ROLES.superAdmin
                      ).length > 0 && (
                        <LineTreeItem
                          onClick={() => handleGlobalLine(0)}
                          classes={{
                            root: layout,
                            selected: layout
                          }}
                          nodeId={'L0'}
                          label={
                            <Box display="flex" alignItems="center" pl={3}>
                              <Typography variant="body2">{t('without-line')}</Typography>
                            </Box>
                          }
                        />
                      )}
                    </StyledTreeItem>
                  )}
                </>
              ))}
          </StyledTreeView>
        </Collapse>
      )}
    </BorderSideNav>
  )
}

export default BrandFilter
